import { CreateEventDto, EventDto, EventPartialDto, LastWWDto, WWRegistrationDto } from "../types/eventTypes";
import api from "../utils/api";
import { AxiosResponse } from "axios";

/**
 * Get all events with workshops as EventPartialDto for the EventsOverview page
 * @returns all events and workshop instances
 */
export const getAllEvents = async (): Promise<AxiosResponse<EventPartialDto[]>> => {
  return await api.get<EventPartialDto[]>(`/events`);
};

/**
 * Get all events and workshop instances for the current user as EventPartialDto for the EventsOverview page
 * @returns all events and workshop instances for the current user
 */
export const getEventsByMemberId = async (): Promise<AxiosResponse<EventPartialDto[]>> => {
  return await api.get<EventPartialDto[]>(`/events/me`);
};

/**
 * Get the event with the given `eventId` as EventDto for the EventDetails page
 * @param event The ID of the event to get
 * @returns the event with the given `eventId`
 */
export const createEvent = async (event: CreateEventDto): Promise<void> => {
  const res = await api.post(`/events`, event);
  return res.data;
};

/**
 * Get the event with the given `eventId` as EventDto for the EventDetails page
 * @param eventId The ID of the event to get
 * @returns the event with the given `eventId`
 */
export const getEventById = async (eventId: number): Promise<AxiosResponse<EventDto>> => {
  return await api.get<EventDto>(`/events/${eventId}`);
};

/**
 * Get the last working weekend event as EventDto for the EventDetails page
 * @returns the last working weekend event or null if there is no working weekend event yet
 */
export const getLastWW = async (): Promise<AxiosResponse<LastWWDto | null>> => {
  return await api.get<LastWWDto | null>(`/events/working-weekend/last-ww`);
};

/**
 * Update the event with the given `eventId` with the given `event`
 * @param event The event to update, must contain the ID of the event to update
 * @returns the updated event
 */
export const updateEvent = async (event: EventDto): Promise<void> => {
  const res = await api.patch(`/events/${event.eventId}`, event);
  return res.data;
};

/**
 * Sign up the current user for an event
 * @param eventId The ID of the event to sign up for
 * @param participantMemberId The ID of the member to sign up for the event
 */
export const signUpForEvent = async (eventId: number, participantMemberId: number): Promise<void> => {
  await api.post(`/events/${eventId}/members`, {
    participantMemberId: participantMemberId,
  });
};

/**
 * Sig out the current user from an event
 * @param eventId The ID of the event to sign out from
 * @param participantMemberId The ID of the member to sign out from the event
 */
export const signOutFromEvent = async (eventId: number, participantMemberId: number): Promise<void> => {
  await api.delete(`/events/${eventId}/members`, {
    data: { participantMemberId },
  });
};

/**
 * Delete the event with the given `eventId`
 * @param eventId The ID of the event to delete
 */
export const deleteEvent = async (eventId: number): Promise<void> => {
  await api.delete(`/events/${eventId}`);
};

/**
 * Sign up the current user for a working weekend with the given `wwRegistration` information
 * @param eventId The ID of the working weekend to sign up for
 * @param memberId The ID of the member to sign up for the working weekend
 * @param wwRegistration The information for the working weekend registration
 */
export const signUpForWorkingWeekend = async (
  eventId: number,
  memberId: number,
  wwRegistration: WWRegistrationDto
): Promise<void> => {
  await api.post(`/events/working-weekend/${eventId}/members/${memberId}`, wwRegistration);
};
